import React, { useState } from 'react';
import { View } from 'react-native';
import { useToastStore } from '@/store/toastStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ThemedText } from '@/components/themed-text';
import { useChargeRefund } from '@/hooks/use-charge-refund';
import { Charge } from '@/types/charge';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { PAYMENT_METHOD } from '@/types/payment-method';
import { CHARGE_ACTIONS } from '@/content/payments/charge';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatCurrency } from '@/utils/format-currency';
import { parseCurrency } from '@/utils/parse-currency';
import { Ionicons } from '@expo/vector-icons';

interface RefundModalProps {
  charge: Charge;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function RefundModal({ charge, open, onOpenChange, onSuccess }: RefundModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const refundMutation = useChargeRefund();
  const { addToast } = useToastStore();

  const chargeAmount = charge.amount;
  const refundedAmount = charge.refundedAmount || 0;
  const availableToRefund = chargeAmount - refundedAmount;
  const isAutomatic = charge.processingMethod === PROCESSING_METHOD.PLATFORM;
  const paymentMethodUsed = charge.paymentMethodUsed;
  // Boleto não tem devolução automática pelo Stripe (plano 6.6) — nem mostramos o formulário.
  const isBoletoRefund = isAutomatic && paymentMethodUsed === PAYMENT_METHOD.BANK_SLIP;
  // Só um aviso — quem recusa de verdade é a API (T2.5). 90 dias é a janela de devolução do Pix.
  const PIX_REFUND_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
  const isPixOverdue =
    isAutomatic &&
    paymentMethodUsed === PAYMENT_METHOD.PIX &&
    !!charge.paidAt &&
    Date.now() - new Date(charge.paidAt).getTime() > PIX_REFUND_WINDOW_MS;

  const handleRefund = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      addToast('Informe um valor válido para o reembolso.', 'error');
      return;
    }

    const refundValue = parseCurrency(amount);

    if (refundValue > availableToRefund) {
      addToast(
        `O valor do reembolso não pode ser maior que ${formatCurrency(availableToRefund)}.`,
        'error',
      );
      return;
    }

    try {
      const result = await refundMutation.mutateAsync({
        chargeId: charge.id,
        amount: refundValue,
        reason: reason || undefined,
      });

      addToast(
        result.refundPending
          ? CHARGE_ACTIONS.refundPendingMessage
          : CHARGE_ACTIONS.refundSuccessMessage,
        'success',
      );
      setAmount('');
      setReason('');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Não foi possível processar o reembolso.'), 'error');
    }
  };

  const handleFullRefund = () => {
    setAmount(
      formatCurrency(availableToRefund)
        .replace(/[^\d,.-]/g, '')
        .replace(',', '.'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <ThemedText className="font-bold text-xl">
              {isAutomatic ? CHARGE_ACTIONS.refund : 'Processar Reembolso'}
            </ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-foreground/60 text-sm">
              Reembolse total ou parcialmente esta cobrança
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <View className="mt-4 gap-4">
          {/* Charge Info */}
          <View className="bg-muted/30 rounded-lg border border-border p-4">
            <View className="mb-2 flex-row items-center justify-between">
              <ThemedText className="text-foreground/60 text-sm">Valor da Cobrança</ThemedText>
              <ThemedText className="font-semibold text-base">
                {formatCurrency(chargeAmount)}
              </ThemedText>
            </View>
            {refundedAmount > 0 && (
              <View className="mb-2 flex-row items-center justify-between">
                <ThemedText className="text-foreground/60 text-sm">Já Reembolsado</ThemedText>
                <ThemedText className="font-semibold text-base text-warning-600">
                  {formatCurrency(refundedAmount)}
                </ThemedText>
              </View>
            )}
            <View className="flex-row items-center justify-between border-t border-border pt-2">
              <ThemedText className="font-medium text-sm">Disponível para Reembolso</ThemedText>
              <ThemedText className="font-bold text-lg text-primary">
                {formatCurrency(availableToRefund)}
              </ThemedText>
            </View>
          </View>

          {/* Explica prazo e taxa antes do valor, só em cobrança automática (T6.3) */}
          {isAutomatic && (
            <View className="gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
              <View className="flex-row items-center gap-2">
                <Ionicons name="information-circle-outline" size={16} className="text-primary" />
                <ThemedText className="font-medium text-xs text-primary">
                  {isBoletoRefund
                    ? 'Não é possível devolver por aqui'
                    : 'Como funciona a devolução'}
                </ThemedText>
              </View>
              {isBoletoRefund ? (
                <ThemedText className="text-foreground/70 text-xs leading-5">
                  {CHARGE_ACTIONS.refundBoletoUnavailable}
                </ThemedText>
              ) : (
                <>
                  <ThemedText className="text-foreground/70 text-xs leading-5">
                    {CHARGE_ACTIONS.refundAutomaticExplanation}
                  </ThemedText>
                  {/* Método desconhecido: cobrança automática paga antes deste campo existir (sem backfill). */}
                  {paymentMethodUsed == null && (
                    <ThemedText className="text-foreground/70 text-xs leading-5">
                      {CHARGE_ACTIONS.refundBoletoUnavailable}
                    </ThemedText>
                  )}
                  {isPixOverdue && (
                    <ThemedText className="text-xs leading-5 text-warning-600">
                      {CHARGE_ACTIONS.refundPixOverdueWarning}
                    </ThemedText>
                  )}
                </>
              )}
            </View>
          )}

          {!isBoletoRefund && (
            <>
              {/* Amount Input */}
              <View className="gap-2">
                <View className="flex-row items-center justify-between">
                  <ThemedText className="font-medium text-sm">Valor do Reembolso</ThemedText>
                  <Button variant="outline" size="sm" onPress={handleFullRefund} className="h-8">
                    <ThemedText className="text-xs">Reembolso Total</ThemedText>
                  </Button>
                </View>
                <Input
                  placeholder={formatCurrency(availableToRefund)}
                  value={amount}
                  onChangeText={setAmount}
                  keyboardType="decimal-pad"
                  className="w-full"
                />
                <ThemedText className="text-foreground/60 text-xs">
                  Máximo: {formatCurrency(availableToRefund)}
                </ThemedText>
              </View>

              {/* Reason Input */}
              <View className="gap-2">
                <ThemedText className="font-medium text-sm">Motivo (Opcional)</ThemedText>
                <Textarea
                  placeholder="Descreva o motivo do reembolso..."
                  value={reason}
                  onChangeText={setReason}
                  numberOfLines={3}
                  className="min-h-[80px]"
                />
              </View>

              {/* Preview */}
              {amount && parseCurrency(amount) > 0 && (
                <View className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <View className="mb-2 flex-row items-center gap-2">
                    <Ionicons
                      name="information-circle-outline"
                      size={20}
                      className="text-primary"
                    />
                    <ThemedText className="font-medium text-sm text-primary">
                      Preview do Reembolso
                    </ThemedText>
                  </View>
                  <View className="gap-1">
                    <View className="flex-row items-center justify-between">
                      <ThemedText className="text-foreground/60 text-sm">
                        Valor a reembolsar
                      </ThemedText>
                      <ThemedText className="font-semibold text-base">
                        {formatCurrency(parseCurrency(amount))}
                      </ThemedText>
                    </View>
                    {parseCurrency(amount) < availableToRefund && (
                      <View className="flex-row items-center justify-between">
                        <ThemedText className="text-foreground/60 text-sm">Restante</ThemedText>
                        <ThemedText className="text-sm">
                          {formatCurrency(availableToRefund - parseCurrency(amount))}
                        </ThemedText>
                      </View>
                    )}
                  </View>
                </View>
              )}
            </>
          )}
        </View>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onPress={() => {
              setAmount('');
              setReason('');
              onOpenChange(false);
            }}
            disabled={refundMutation.isPending}
          >
            <ThemedText>{isBoletoRefund ? 'Fechar' : 'Cancelar'}</ThemedText>
          </Button>
          {!isBoletoRefund && (
            <Button
              onPress={handleRefund}
              disabled={refundMutation.isPending || !amount || parseCurrency(amount) <= 0}
              className="bg-primary"
            >
              {refundMutation.isPending ? (
                <ThemedText>Processando...</ThemedText>
              ) : (
                <ThemedText>Confirmar Reembolso</ThemedText>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
